import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';

import { ApiError } from '../api/client.js';
import { SessionProvider, useSession } from '../api/session.js';
import { ToastProvider } from '../components/Toast.js';
import { pollInterval } from '../live/useLiveUpdates.js';
import AlertDetailPage from '../pages/AlertDetailPage.js';
import AlertsPage from '../pages/AlertsPage.js';
import ComparePage from '../pages/ComparePage.js';
import ConnectorsPage from '../pages/ConnectorsPage.js';
import DashboardPage from '../pages/DashboardPage.js';
import LoginPage from '../pages/LoginPage.js';

import { AppShell } from './AppShell.js';

// Routes (plan Phases 10 and 11). The kitchen sink at `/__ui` exists only in dev builds.
const KitchenSinkPage = import.meta.env.DEV
  ? lazy(() => import('../pages/KitchenSinkPage.js'))
  : null;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchInterval: pollInterval,
        refetchOnWindowFocus: true,
        staleTime: 5_000,
        // A 4xx won't get better by asking again.
        retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      },
    },
  });
}

function RequireSession() {
  const { state } = useSession();
  const location = useLocation();
  if (state.status === 'loading') return null;
  if (state.status === 'out') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <AppShell />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {KitchenSinkPage ? (
        <Route
          path="/__ui"
          element={
            <Suspense fallback={null}>
              <KitchenSinkPage />
            </Suspense>
          }
        />
      ) : null}
      <Route element={<RequireSession />}>
        <Route index element={<DashboardPage />} />
        <Route path="/alerts" element={<AlertsPage />} />
        <Route path="/alerts/:id" element={<AlertDetailPage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/connectors" element={<ConnectorsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App({ queryClient = createQueryClient() }: { queryClient?: QueryClient }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <SessionProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </SessionProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
